import { useEffect, useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from "@/components/ui/pagination";
import { Users, BookOpen, TrendingUp, Zap, CreditCard, Tag, ArrowUpDown, ArrowUp, ArrowDown, Clock, Gift } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCreditSystem } from "@/hooks/useCreditSystem";
import { useTestClock } from "@/contexts/TestClockContext";
import { safeLog } from '@/utils/safeLogger';

interface UserTransaction {
  id: string;
  action_type: string;
  baseline_credits: number;
  openai_usage_credits: number;
  final_credits: number;
  credits_before: number;
  credits_after: number;
  transaction_id: string;
  created_at: string;
  context_metadata?: {
    totalTokens?: number;
    features?: {
      extraction?: boolean;
      smartCategories?: boolean;
      smartNotes?: boolean;
    };
    tier?: string;
    package?: string;
    expired_additional_credits?: number;
  };
}

export function UserCreditUsage() {
  const { isTestMode, getCurrentTime, clockOffset } = useTestClock();
  const [transactions, setTransactions] = useState<UserTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortField, setSortField] = useState<'created_at' | 'credits_before' | 'final_credits' | 'credits_after'>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const itemsPerPage = 50;
  
  const { stats } = useCreditSystem();
  const { totalUsed, currentBalance } = stats;

  useEffect(() => {
    loadUserTransactions();

    // Set up real-time subscription only for transaction history (not credit balance)
    const channel = supabase
      .channel('user-credit-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'credit_transactions'
        },
        (payload) => {
          console.log('[UserCreditUsage] New transaction:', payload);
          const newTxn = payload.new as UserTransaction;
          setTransactions(prev => [newTxn, ...prev].slice(0, itemsPerPage));
          // Reload all transactions to ensure consistency
          setTimeout(() => loadUserTransactions(), 500);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  async function loadUserTransactions() {
    try {
      setLoading(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch total count
      const { count } = await supabase
        .from('credit_transactions')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      setTotalCount(count || 0);

      // Fetch paginated transactions
      const from = (currentPage - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;

      const { data: txns, error } = await supabase
        .from('credit_transactions')
        .select('id, action_type, baseline_credits, openai_usage_credits, final_credits, credits_before, credits_after, transaction_id, created_at, context_metadata')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) {
        console.error('Error loading transactions:', error);
        return;
      }

      setTransactions(txns as UserTransaction[] || []);

    } catch (error) {
      console.error('Error loading user transactions:', error);
    } finally {
      setLoading(false);
    }
  }

  const getFeatureBadges = (txn: UserTransaction) => {
    // For topup and subscription - show as before
    if (txn.action_type === 'topup') {
      const packageType = txn.context_metadata?.package || 'Credits';
      return (
        <Badge className="gap-1 bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900 dark:text-purple-200">
          <TrendingUp className="h-3 w-3" />
          Top-up ({packageType})
        </Badge>
      );
    }
    
    if (txn.action_type === 'subscription_purchase') {
      const tier = txn.context_metadata?.tier || '';
      const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);
      return (
        <Badge className="gap-1 bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900 dark:text-blue-200">
          <Zap className="h-3 w-3" />
          New Subscription ({tierLabel})
        </Badge>
      );
    }

    if (txn.action_type === 'subscription_renewal') {
      const tier = txn.context_metadata?.tier || '';
      const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);
      const expiredCredits = txn.context_metadata?.expired_additional_credits;
      
      // Always show the renewal badge
      const badges = [
        <Badge key="renewal" className="gap-1 bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900 dark:text-blue-200">
          <Zap className="h-3 w-3" />
          Subscription Renewed ({tierLabel})
        </Badge>
      ];
      
      // Add expiration badge if credits expired
      if (expiredCredits && expiredCredits > 0) {
        badges.push(
          <Badge key="expired" className="gap-1 bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900 dark:text-orange-200">
            <Clock className="h-3 w-3" />
            Credits Expired ({tierLabel})
          </Badge>
        );
      }
      
      return <>{badges}</>;
    }

    // Plan renewal expiration
    if (txn.action_type === 'plan_renewal_expiration') {
      return (
        <Badge className="gap-1 bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900 dark:text-orange-200">
          <Zap className="h-3 w-3" />
          Additional Credits Expired
        </Badge>
      );
    }

    // Plan change expiration
    if (txn.action_type === 'plan_change_expiration') {
      return (
        <Badge className="gap-1 bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900 dark:text-purple-200">
          <Zap className="h-3 w-3" />
          Credits Expired (Plan Change)
        </Badge>
      );
    }

    // For contact_processing - show multiple badges based on features
    if (txn.action_type === 'contact_processing' && txn.context_metadata?.features) {
      const features = txn.context_metadata.features;
      const badges = [];

      if (features.extraction) {
        badges.push(
          <Badge key="extraction" variant="default" className="gap-1">
            <Users className="h-3 w-3" />
            Contact Extracted
          </Badge>
        );
      }

      if (features.smartCategories) {
        badges.push(
          <Badge key="categories" variant="secondary" className="gap-1">
            <Tag className="h-3 w-3" />
            Smart Categories
          </Badge>
        );
      }

      if (features.smartNotes) {
        badges.push(
          <Badge key="notes" variant="outline" className="gap-1">
            <BookOpen className="h-3 w-3" />
            Smart Notes
          </Badge>
        );
      }

      return <div className="flex gap-1 flex-wrap">{badges}</div>;
    }

    // Legacy support for old action types
    if (txn.action_type === 'contact_extraction') {
      return (
        <Badge variant="default" className="gap-1">
          <Users className="h-3 w-3" />
          Contact Extracted
        </Badge>
      );
    }

    if (txn.action_type === 'smart_notes' || txn.action_type === 'smart_notes_research') {
      return (
        <Badge variant="outline" className="gap-1">
          <BookOpen className="h-3 w-3" />
          Smart Notes
        </Badge>
      );
    }

    // Welcome bonus
    if (txn.action_type === 'welcome_bonus') {
      return (
        <Badge className="gap-1 bg-green-100 text-green-800 border-green-300 dark:bg-green-900 dark:text-green-200">
          <Gift className="h-3 w-3" />
          Welcome Credits
        </Badge>
      );
    }

    // Default fallback
    return (
      <Badge variant="outline" className="gap-1">
        <CreditCard className="h-3 w-3" />
        {txn.action_type}
      </Badge>
    );
  };

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }: { field: typeof sortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-4 h-4 ml-1" />;
    }
    return sortDirection === 'asc' ? 
      <ArrowUp className="w-4 h-4 ml-1" /> : 
      <ArrowDown className="w-4 h-4 ml-1" />;
  };

  const sortedTransactions = useMemo(() => {
    return [...transactions].sort((a, b) => {
      let aValue: number | Date;
      let bValue: number | Date;

      if (sortField === 'created_at') {
        aValue = new Date(a.created_at);
        bValue = new Date(b.created_at);
      } else {
        aValue = a[sortField] || 0;
        bValue = b[sortField] || 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [transactions, sortField, sortDirection]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Credit History</CardTitle>
          <CardDescription>Loading transactions...</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Credit History</CardTitle>
            <CardDescription>
              Showing {transactions.length} of {totalCount} transactions
            </CardDescription>
          </div>
          <div className="flex gap-6 bg-muted/30 px-4 py-3 rounded-lg">
            <div className="text-right">
              <div className="text-sm text-muted-foreground">Current Balance</div>
              <div className="text-2xl font-bold text-primary">
                {currentBalance !== null ? Math.round(currentBalance) : '—'} Credits
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-muted-foreground">Total Used</div>
              <div className="text-2xl font-bold text-muted-foreground">
                {Math.round(totalUsed)} Credits
              </div>
            </div>
          </div>
        </div>
        
        {isTestMode && (
          <Alert className="mt-4 border-blue-500 bg-blue-50 dark:bg-blue-950">
            <Clock className="h-4 w-4 text-blue-600" />
            <AlertDescription>
              <strong>🧪 Test Mode Active:</strong> All timestamps are relative to simulated time.
              <br />
              <span className="text-xs">
                Current simulated time: {format(getCurrentTime(), 'PPP HH:mm:ss')}
              </span>
            </AlertDescription>
          </Alert>
        )}
      </CardHeader>
      <CardContent>
        {transactions.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            No transactions yet
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center">
                      Date
                      <SortIcon field="created_at" />
                    </div>
                  </TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('credits_before')}
                  >
                    <div className="flex items-center justify-end">
                      Before
                      <SortIcon field="credits_before" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('final_credits')}
                  >
                    <div className="flex items-center justify-end">
                      Amount
                      <SortIcon field="final_credits" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('credits_after')}
                  >
                    <div className="flex items-center justify-end">
                      After
                      <SortIcon field="credits_after" />
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedTransactions.map((txn) => {
                  const isCredit = txn.action_type === 'topup' || 
                                 txn.action_type === 'subscription_purchase' || 
                                 txn.action_type === 'welcome_bonus' ||
                                 (txn.action_type === 'subscription_renewal' &&
                                  (!txn.context_metadata?.expired_additional_credits || 
                                   txn.context_metadata.expired_additional_credits === 0));
                  const isExpiration = txn.action_type === 'plan_renewal_expiration' || 
                                      txn.action_type === 'plan_change_expiration' ||
                                      (txn.action_type === 'subscription_renewal' && 
                                       txn.context_metadata?.expired_additional_credits && 
                                       txn.context_metadata.expired_additional_credits > 0);
                  return (
                    <TableRow key={txn.id}>
                      <TableCell className={cn(
                        "text-sm",
                        isTestMode && "text-blue-600 dark:text-blue-400 font-medium"
                      )}>
                        {format(
                          isTestMode 
                            ? new Date(new Date(txn.created_at).getTime() + clockOffset)
                            : new Date(txn.created_at),
                          'MMM dd, yyyy HH:mm'
                        )}
                        {isTestMode && <span className="text-xs ml-1">(sim)</span>}
                      </TableCell>
                      <TableCell>
                        {getFeatureBadges(txn)}
                      </TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">
                        {txn.credits_before ?? '—'}
                      </TableCell>
                      <TableCell className={cn(
                        "text-right font-medium",
                        isCredit
                          ? "text-green-600 dark:text-green-400"
                          : isExpiration
                          ? "text-orange-600 dark:text-orange-400"
                          : "text-red-600 dark:text-red-400"
                      )}>
                        {isCredit
                          ? `+${Math.round(txn.final_credits)}`
                          : isExpiration
                          ? `-${Math.abs(Math.round(txn.final_credits))}`
                          : `-${Math.abs(Math.round(txn.final_credits))}`
                        }
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {txn.credits_after ?? '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {totalCount > itemsPerPage && (
          <div className="mt-4">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious 
                    onClick={() => {
                      setCurrentPage(p => Math.max(1, p - 1));
                      loadUserTransactions();
                    }}
                    className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    return (
                      page === 1 || 
                      page === totalPages || 
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    );
                  })
                  .map((page, idx, arr) => (
                    <span key={page}>
                      {idx > 0 && arr[idx - 1] !== page - 1 && (
                        <PaginationItem>
                          <PaginationEllipsis />
                        </PaginationItem>
                      )}
                      <PaginationItem>
                        <PaginationLink
                          onClick={() => {
                            setCurrentPage(page);
                            setTimeout(loadUserTransactions, 0);
                          }}
                          isActive={currentPage === page}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    </span>
                  ))
                }
                
                <PaginationItem>
                  <PaginationNext 
                    onClick={() => {
                      setCurrentPage(p => Math.min(totalPages, p + 1));
                      loadUserTransactions();
                    }}
                    className={currentPage >= totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
